import type React from 'react';
import { memo } from 'react';
import styled from 'styled-components';

import { backgroundColorsByType, colorsByType, namesByType } from '@front/common/src/components/alert/constants';
import { useClosableAlert } from '@front/common/src/components/alert/hooks';
import {
  alertCloseButtonStyle,
  alertContainerStyle,
  alertIconStyle,
  alertTitleStyle,
} from '@front/common/src/components/alert/styles';
import type {
  AlertCommonProps,
  AlertContainerProps,
  AlertIconProps,
  AlertType,
} from '@front/common/src/components/alert/types';

import { translate } from '@front/i18n';

import Text, { type TextProps } from '../base-text/base-text';
import IconButton from '../button/icon-button';
import Icon from '../icon/icon';
import ContentAlert from './content-alert';

const t = translate('alerts.default');

// @deprecated
export const TYPES: Record<string, AlertType> = {
  ERROR: 'error',
  INFO: 'info',
  SUCCESS: 'success',
};

const Title = styled(Text)`
  ${alertTitleStyle}
`;

const StyledIcon = styled(Icon)<AlertIconProps>`
  ${alertIconStyle}
`;

const CloseButton = styled(IconButton)`
  ${alertCloseButtonStyle}
`;

const Container = styled.div<AlertContainerProps>`
  ${alertContainerStyle}
`;

export type RenderProps = Pick<TextProps, 'fontSize' | 'fontWeight' | 'color'>;
export type AlertProps = AlertCommonProps & {
  className?: string;
  content: string | string[] | Error | ((props: RenderProps) => React.ReactElement) | React.ReactElement;
};

const Alert = ({
  title,
  content,
  type,
  className,
  hasIcon = true,
  centered = false,
  isClosable = false,
  onClose,
  testID,
}: AlertProps) => {
  const { isClosed, onPress } = useClosableAlert(onClose);

  if (isClosed) return null;

  return (
    <Container
      data-testid={testID}
      role='alert'
      $color={backgroundColorsByType[type]}
      $type={type}
      className={className}
      $hasPaddingForIcon={hasIcon}
      $isClosable={isClosable}
    >
      {title && (
        <Title fontSize={13} fontWeight={700} color={colorsByType[type]} display='block' centered={centered}>
          {title}
        </Title>
      )}
      <ContentAlert type={type} content={content} centered={centered} />
      {hasIcon && (
        <StyledIcon name={namesByType[type]} size={15} color={colorsByType[type]} $isRotated={type === 'info'} />
      )}
      {isClosable && (
        <CloseButton
          type={type === 'error' ? 'danger' : type}
          label={t('.buttons.close')}
          onPress={onPress}
          size={20}
          iconName='no'
          iconSize={10}
        />
      )}
    </Container>
  );
};

export default memo(Alert);
